import { KPI_SOURCE_KINDS } from '../kpi'
import type { BuiltInTool, JsonSchema } from './types'

/**
 * os#897: a run lists and records readings for the signed-off KPIs its teams own, plus any signed-off KPI whose
 * source is its task. Recording only — neither tool lets a run create, edit, sign off, or change a target, band,
 * cadence or source.
 *
 * Both work on the one KPI store every surface reads (the `kpis`/`kpi_readings` tables behind HQ's board, the API's
 * KPI paths and the MCP server's KPI tools), so a reading a run records moves the light all of them show.
 */
export const OS_KPI_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'os.list_kpis',
        family: 'os.kpi',
        summary: "List the signed-off KPIs the run's teams own and those its task is the source of, with their lights.",
        description:
            "List the KPIs this run may read: the KPIs of every team its member sits on or leads, and any KPI whose source is this run's task, whichever layer owns it. Only KPIs a person has signed off are listed. Each row carries its target (value, direction, AMBER band, on-target-worsening rule), cadence, owner (layer and id), source (kind and ref), latest reading, current light and whether it is overdue. The light is null when the KPI has no target or no reading yet. No arguments. Never a KPI of a team the member is not on unless its source is this run's task, and never another enterprise's.",
        inputSchema: { type: 'object', properties: {}, additionalProperties: false },
        returns:
            '`{ data }`: one row per KPI, each with `id`, `name`, `ownerLayer` (`company`, `team`, `role`, `task`, or null for a company KPI set before layers), `ownerId`, `sourceKind` (`task` or `person`), `sourceRef` (the task or person id), `target`, `cadenceMinutes`, `light`, `overdue` and `lastReading` (`id`, `value`, `takenAt`, `recordedBy`, `runId`), or null before the first reading.',
        callers: ['run'],
        effect: 'read',
        retry: 'safe',
        errors: [],
        examples: [{ input: {} }],
        public: true,
    },
    {
        name: 'os.record_kpi_reading',
        family: 'os.kpi',
        summary: "Record a reading for a KPI whose source is this run's task.",
        description:
            "Record one reading for the KPI named by kpiId (from os.list_kpis). Only a run of the task that KPI names as its source (sourceKind task, sourceRef this run's task) may record one, and only once a person has signed the KPI off. Pass value and, optionally, takenAt (ISO 8601; defaults to now). No other field is accepted: no os.* tool lets a run change a target, band, cadence or source. The reading is recorded as taken by this run (`recordedBy: run:<runId>`); a run cannot record on another run's behalf. It lands in the same history HQ's board and the KPI readings API read, so the light they show moves with it.",
        inputSchema: {
            type: 'object',
            properties: {
                kpiId: { type: 'string', description: 'A KPI id from os.list_kpis' },
                value: { type: 'number' },
                takenAt: { type: 'string', description: 'ISO 8601. Defaults to now.' },
            },
            required: ['kpiId', 'value'],
            additionalProperties: false,
        },
        returns:
            'The recorded reading and the KPI it landed on: `kpiId`, `name`, `readingId`, `value`, `takenAt`, `recordedBy` and `runId` (this run), `target`, and the KPI’s `light` as it now stands.',
        callers: ['run'],
        effect: 'write',
        retry: 'idempotent',
        retryNote:
            'Keyed on the tool call, like every other os.* write tool: a repeat of the same call records once and returns the same reading.',
        errors: [
            { code: 'kpi_id_required', when: '`kpiId` is missing.', handling: 'tool_error' },
            { code: 'value_required', when: '`value` is missing or not a number.', handling: 'tool_error' },
            {
                code: 'taken_at_invalid',
                when: '`takenAt` is not a timestamp anything can read.',
                handling: 'tool_error',
            },
            {
                code: 'kpi_not_found',
                when: "`kpiId` does not name a KPI this run may record: another enterprise's, one whose source is not this run's task, or one nobody has signed off yet.",
                handling: 'tool_error',
            },
        ],
        examples: [{ input: { kpiId: '5f0c1d2e-0000-4000-8000-000000000009', value: 42 } }],
        public: true,
    },
]

const KPI_NOT_FOUND = {
    code: 'kpi_not_found',
    when: "`kpiId` names no KPI in this enterprise, another enterprise's included.",
    handling: 'tool_error',
} as const

/**
 * #896's hand-entered readings, for a caller outside a run: the MCP server serves both over the KPI readings API
 * (#1011), under the names its clients already call, which is why they sit outside the `os.` namespace. A run
 * records through `os.record_kpi_reading` above instead, as itself. Readings are append-only: neither tool edits
 * or deletes one.
 */
export const KPI_READING_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'kpi.record_reading',
        family: 'os.kpi',
        summary: 'Record one reading of a KPI by hand: the value, when it was taken, and who or what took it.',
        description:
            'Record one reading of a KPI by hand: the value, when it was taken, and who or what took it. Every ' +
            'reading is kept — correcting a wrong one means recording a new reading, never editing or deleting ' +
            'the old one — so a KPI history shows how it got to where it is. The light and the trend are read ' +
            'off the readings, so recording one is what moves them. A KPI nobody has signed off takes no ' +
            'reading: it is refused with 409 kpi_not_signed_off.',
        inputSchema: {
            type: 'object',
            properties: {
                kpiId: { type: 'string', description: 'The KPI the reading belongs to.' },
                value: { type: 'number', description: "The number read, in the KPI's own unit." },
                takenAt: {
                    type: 'string',
                    description: 'When the reading was taken, as an ISO 8601 timestamp, e.g. 2026-01-08T00:00:00Z.',
                },
                recordedBy: {
                    type: 'string',
                    description: 'Who or what took the reading: a member id, or the agent or tool that read it.',
                },
            },
            required: ['kpiId', 'value', 'takenAt', 'recordedBy'],
            additionalProperties: false,
        },
        returns: 'The reading as recorded: `id`, `kpiId`, `value`, `takenAt` (ISO 8601) and `recordedBy`.',
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'Every call appends a reading, so a repeat records a second one.',
        errors: [
            KPI_NOT_FOUND,
            {
                code: 'kpi_not_signed_off',
                when: 'Nobody has signed the KPI off yet, so it takes no reading.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/kpis/:kpiId/readings', scope: 'kpis:write' },
        examples: [
            {
                input: {
                    kpiId: '5f0c1d2e-0000-4000-8000-000000000009',
                    value: 3.5,
                    takenAt: '2026-01-08T00:00:00Z',
                    recordedBy: 'weekly-metrics-export',
                },
            },
        ],
        public: true,
    },
    {
        name: 'kpi.list_readings',
        family: 'os.kpi',
        summary: "One KPI's readings, newest first, corrected ones included.",
        description:
            "One KPI's readings, newest first: the value, when it was taken and who or what took it. This is the " +
            "whole history behind the KPI's light and its trend, including any reading a later one corrected.",
        inputSchema: {
            type: 'object',
            properties: { kpiId: { type: 'string', description: 'The KPI whose readings to list.' } },
            required: ['kpiId'],
            additionalProperties: false,
        },
        returns:
            'Every reading of the KPI, newest by `takenAt` first, each with `id`, `kpiId`, `value`, `takenAt` and `recordedBy`.',
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [KPI_NOT_FOUND],
        api: { method: 'GET', path: '/v1/kpis/:kpiId/readings', scope: 'kpis:read' },
        examples: [{ input: { kpiId: '5f0c1d2e-0000-4000-8000-000000000009' } }],
        public: true,
    },
]

/** What sets a role KPI: the role, and the fields a KPI on every layer is set by, as the API takes them. */
function roleKpiArguments(): Record<string, JsonSchema> {
    return {
        roleId: { type: 'string', description: 'The role the KPI belongs to.' },
        name: { type: 'string', description: 'What is measured, up to 200 characters.' },
        targetValue: {
            type: 'number',
            description:
                'The target, in the KPI’s own unit. Set it with targetDirection, or leave both out for no target.',
        },
        targetDirection: {
            type: 'string',
            enum: ['at_least', 'at_most'],
            description: 'at_least when higher is better, at_most when lower is better.',
        },
        amberBand: {
            type: 'number',
            minimum: 0,
            description: 'How far past the target still reads AMBER rather than RED.',
        },
        onTargetWorseningRule: {
            type: 'boolean',
            description: 'true to read AMBER when on target but worse than the reading before.',
        },
        cadenceMinutes: {
            type: 'integer',
            minimum: 1,
            description: 'How often it is read, in minutes; a reading older than this is overdue.',
        },
        servesKpiId: { type: 'string', description: 'The KPI of the role’s own team this one serves.' },
        sourceKind: {
            type: 'string',
            enum: KPI_SOURCE_KINDS,
            description: 'Where readings come from: task (a task records them) or person (entered by hand).',
        },
        sourceRef: { type: 'string', description: 'The task id, or the person’s human-user id, sourceKind names.' },
    }
}

const ROLE_NOT_FOUND = {
    code: 'role_not_found',
    when: "`roleId` names no role in this enterprise, another enterprise's included.",
    handling: 'tool_error',
} as const

/**
 * #901: a role's KPIs, for a caller outside a run, over the API's `roles/:roleId/kpis` routes. The MCP server serves
 * them from here (#1011). There is no tool to sign one off or to withdraw one that is signed off: a person does that
 * from their own session, and a token is refused there.
 */
export const ROLE_KPI_TOOLS: readonly BuiltInTool[] = [
    {
        name: 'role.list_kpis',
        family: 'os.kpi',
        summary: "A role's KPIs, oldest first, with the team KPI each serves, its sign-off, light, trend and readings.",
        description:
            "A role's KPIs, oldest first: each one's target, direction, AMBER band, cadence, source, the team KPI it " +
            'serves (servesName names it; served is false for one that serves none), whether a person has signed it ' +
            'off, its light and trend (null until it is signed off) and its readings. Not found for a role in another ' +
            'enterprise.',
        inputSchema: {
            type: 'object',
            properties: { roleId: { type: 'string', description: 'The role whose KPIs to list.' } },
            required: ['roleId'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'read',
        retry: 'safe',
        errors: [ROLE_NOT_FOUND],
        api: { method: 'GET', path: '/v1/roles/:roleId/kpis', scope: 'roles:read' },
        public: true,
    },
    {
        name: 'role.define_kpi',
        family: 'os.kpi',
        summary: 'Propose a KPI on a role, waiting for a person to sign it off.',
        description:
            'Propose a KPI on a role, with the same fields as a KPI on any other layer. It waits for a person to sign ' +
            'it off, and until then draws no light and takes no reading. A KPI may serve only a team’s KPI, any team’s, ' +
            'since a role is held across teams (400 role_kpi_serves_non_team otherwise); leave servesKpiId out for ' +
            'one that serves none. The ' +
            "response is the role's KPIs after the change.",
        inputSchema: {
            type: 'object',
            properties: roleKpiArguments(),
            required: ['roleId', 'name'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'unsafe',
        retryNote: 'Every call adds a KPI to the role, so a repeat proposes a second one.',
        errors: [
            ROLE_NOT_FOUND,
            {
                code: 'role_kpi_serves_non_team',
                when: '`servesKpiId` names a KPI that is not a team’s: the company’s, a role’s or a task’s.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'POST', path: '/v1/roles/:roleId/kpis', scope: 'roles:write' },
        public: true,
    },
    {
        name: 'role.edit_kpi',
        family: 'os.kpi',
        summary: "Change a role KPI's fields; a signed-off one only a person changes.",
        description:
            "Change a role KPI's fields; the ones left out keep their value. A KPI a person has signed off is changed " +
            "only by a person, so this is refused with 403 kpi_signed_off_person_only for it. The response is the role's " +
            'KPIs after the change.',
        inputSchema: {
            type: 'object',
            properties: {
                ...roleKpiArguments(),
                kpiId: { type: 'string', description: 'The KPI to change.' },
            },
            required: ['roleId', 'kpiId'],
            additionalProperties: false,
        },
        callers: ['api', 'mcp'],
        effect: 'write',
        retry: 'idempotent',
        retryNote: 'The same fields set twice leave the KPI as the first call did.',
        errors: [
            ROLE_NOT_FOUND,
            {
                code: 'role_kpi_not_found',
                when: '`kpiId` names no KPI still on this role.',
                handling: 'tool_error',
            },
            {
                code: 'role_kpi_serves_non_team',
                when: '`servesKpiId` names a KPI that is not a team’s: the company’s, a role’s or a task’s.',
                handling: 'tool_error',
            },
            {
                code: 'kpi_signed_off_person_only',
                when: 'A person has signed the KPI off, so only a person may change it.',
                handling: 'tool_error',
            },
        ],
        api: { method: 'PATCH', path: '/v1/roles/:roleId/kpis/:kpiId', scope: 'roles:write' },
        public: true,
    },
]
